// Strip a single leading list marker / numbering / bullet from a line so we can
// renumber cleanly. Handles: 1  1.  1)  1.2.3  [1]  (1)  <1>  a.  A)  iv.  • - – —
// Numbers may be followed by space alone (e.g. "1 Introduction"); letters/roman
// numerals require trailing punctuation so we don't eat words like "I am".
const NUMBER = String.raw`[(\[<]?\s*\d+(?:[.)]\d+)*\s*[)\].>:]?`;
const LETTER = String.raw`[(\[<]?\s*(?:[ivxlcdm]+|[a-z])\s*[)\].>:]`;
const BULLET = String.raw`[•·▪‣◦∙*‒–—-]`;
const LEADING_MARKER = new RegExp(`^\\s*(?:${NUMBER}|${LETTER}|${BULLET})\\s+`, "i");

export function stripLeadingMarker(text: string): string {
  return text.replace(LEADING_MARKER, "").trimStart();
}

// Word authors sometimes type consecutive bibliography entries in one
// paragraph, e.g. "[3] First reference.[4] Second reference.". Split only a
// sequential bracketed/parenthesized marker chain that starts the paragraph
// and whose later markers follow sentence punctuation. This avoids treating a
// numeric citation inside a reference title as a new bibliography entry.
export function splitNumberedReferences(text: string): string[] {
  const source = text.trim();
  if (!source) return [];

  const candidates: Array<{ index: number; number: number }> = [];
  const marker = /(?:\[\s*(\d{1,4})\s*\]|\(\s*(\d{1,4})\s*\))/g;
  let match: RegExpExecArray | null;
  while ((match = marker.exec(source)) !== null) {
    const before = source.slice(0, match.index);
    if (before.trim() && !/[.!?;:]\s*$/.test(before)) continue;
    candidates.push({ index: match.index, number: Number(match[1] || match[2]) });
  }

  if (candidates.length < 2 || source.slice(0, candidates[0].index).trim()) {
    return [stripLeadingMarker(source)].filter(Boolean);
  }

  const boundaries = [candidates[0]];
  for (const candidate of candidates.slice(1)) {
    if (candidate.number === boundaries[boundaries.length - 1].number + 1) boundaries.push(candidate);
  }
  if (boundaries.length < 2) return [stripLeadingMarker(source)].filter(Boolean);

  return boundaries
    .map((boundary, index) => {
      const end = boundaries[index + 1]?.index ?? source.length;
      return stripLeadingMarker(source.slice(boundary.index, end).trim());
    })
    .filter(Boolean);
}

export function toTitleCase(text: string): string {
  // Word's non-breaking hyphen arrives as U+001E. Treat it as a real hyphen so
  // "AI\u001EDriven" keeps its acronym segment ("AI-Driven") instead of being
  // capitalized as one word and losing the hyphen when the control char is stripped.
  // U+2011 (non-breaking hyphen) / U+2010 (hyphen) are the same story on Mac Word.
  // Hyphen-like characters all become a plain hyphen-minus (U+001E Word nb-hyphen
  // control, U+2010 hyphen, U+2011 non-breaking hyphen, U+2012 figure dash); a
  // soft hyphen is dropped. En dash / em dash / horizontal bar / minus sign are
  // kept as typed but count as compound joiners below (DASH_CLASS).
  text = (text || "").replace(/\u00AD/g, "").replace(/[\u001E\u2010\u2011\u2012]/g, "-");
  if (!text) return "";
  const minorWords = new Set([
    "a", "an", "the",
    "and", "but", "for", "or", "nor",
    "about", "above", "across", "after", "against", "along", "among", "around", "at",
    "before", "behind", "below", "beneath", "beside", "between", "beyond", "by",
    "concerning", "considering", "despite", "down", "during", "except", "from",
    "in", "inside", "into", "like", "near", "of", "off", "on", "onto", "out", "outside",
    "over", "past", "regarding", "since", "through", "throughout", "to", "toward", "under",
    "underneath", "until", "up", "upon", "versus", "with", "within", "without",
    "as", "so", "yet"
  ]);

  const words = text.split(/\s+/);
  const last = words.length - 1;
  // A word is "shouted" if it has letters and they're all uppercase (FIR,
  // DEMOCRACY, USA...). When EVERY word with letters is shouted, the whole
  // line was typed in caps-lock, not a title with one real acronym inside it
  // (e.g. "LITERATURE REVIEW" vs "Revisiting FIR for Better Justice") — in
  // that case normalize every word instead of preserving any of them, or a
  // fully-caps heading would render completely untouched.
  const isShouted = (w: string) => /[A-Z]/.test(w) && w === w.toUpperCase() && w !== w.toLowerCase();
  const wholeLineShouted = words.length > 0 && words.every((w) => !/[a-zA-Z]/.test(w) || isShouted(w));
  // The word starting a new clause after a colon/dash (e.g. "Title: A
  // Subtitle") is always capitalized too, same as the very first word —
  // NYT/AP title case capitalizes the first word of EVERY clause, not just
  // the first word of the whole string. Track this across the loop instead
  // of only checking index 0.
  let startOfClause = true;
  return words
    .map((word, index) => {
      // Remove trailing/leading punctuation for dictionary check
      const cleanWord = word.replace(/[.,\/#!$%\^&\*;:{}=\-\u2013\u2014\u2015\u2212_`~()]/g, "").toLowerCase();
      const forceCap = startOfClause || index === last;
      startOfClause = /[:\u2013\u2014\u2015-]$/.test(word);
      // A pure roman numeral ("II", "IV", "XVI") is conventionally left as
      // written — "Part II" not "Part Ii".
      if (cleanWord && /^[ivxlcdm]+$/.test(cleanWord)) {
        return word.toUpperCase();
      }
      // A hyphenated compound with an acronym segment (e-FIR, non-NGO,
      // e-KYC's) — the whole-word checks below only look at the token as
      // one unit, so "e-FIR" (mixed case overall) never matched and fell
      // through to normal capitalization, mangling it to "E-fir". Preserve
      // the whole hyphenated token exactly as typed whenever any segment is
      // itself an acronym.
      // Compound joiner: hyphen OR en dash typed without spaces ("AI–enabled").
      if (!wholeLineShouted && /[-\u2013\u2014\u2015\u2212]/.test(word)) {
        const parts = word.split(/([-\u2013\u2014\u2015\u2212])/); // words at even indices, joiners at odd
        const segments = parts.filter((_, i) => i % 2 === 0);
        const isAcro = (seg: string) => isShouted(seg) || /^[A-Z]{2,}'?s$/.test(seg);
        if (segments.length > 1 && segments.some(isAcro)) {
          // Acronym segments stay as typed; short prefixes (e-, non-, pre-, co-)
          // stay as typed; any other word segment is capitalized, so
          // "AI-driven" -> "AI-Driven" while "e-FIR" / "non-NGO" are untouched.
          return parts
            .map((seg, i) => (i % 2 === 1 || isAcro(seg) || seg.length <= 3 || !/^[a-z]/.test(seg) ? seg : seg.charAt(0).toUpperCase() + seg.slice(1)))
            .join("");
        }
      }
      // An ALL-CAPS word (FIR, UN, USA, NGO...) sitting inside an otherwise
      // normal-case title is an acronym — leave it exactly as typed. Skip
      // this when the whole line is shouted (handled above).
      if (!wholeLineShouted && cleanWord && isShouted(word)) {
        return word;
      }
      // An acronym with a lowercase plural/possessive suffix (FIRs, NGOs,
      // MPs, FIR's) isn't fully uppercase so isShouted misses it — without
      // this it fell through to the normal path and got mangled to "Firs".
      if (!wholeLineShouted && /^[A-Z]{2,}'?s$/.test(word)) {
        return word;
      }
      const lower = word.toLowerCase();
      if (forceCap || !minorWords.has(cleanWord)) {
        // Capitalize the first LETTER, not literally index 0 — a word
        // wrapped in a leading quote/paren ("Wholly, ("Necessary) has a
        // punctuation mark at index 0, and capitalizing that (a no-op) left
        // the real first letter lowercase ("wholly instead of "Wholly).
        const firstLetter = lower.search(/[a-z]/);
        if (firstLetter === -1) return lower;
        return lower.slice(0, firstLetter) + lower.charAt(firstLetter).toUpperCase() + lower.slice(firstLetter + 1);
      }
      return lower;
    })
    .join(" ");
}
